import {
  FccAdAlertStatus,
  FccOperatorRoleType,
} from '@backoffice-frontend/graphql'
import { compareAsc } from 'date-fns'
import {
  getHandlingOperatorForRole,
  type HandlingOperator,
} from './getHandlingOperatorForRole'
import type {
  OtherRoleActivityAlertFragment,
  OtherRoleActivityVehicleFragment,
} from './getOtherRoleActivity.hook'
import { isAlertVisibleToRole } from './useAlertsForRole'

export type OtherRoleActivity =
  | { state: 'idle' }
  | { state: 'requested'; alerts: OtherRoleActivityAlertFragment[] }
  | {
      state: 'handling'
      operator: HandlingOperator
      alerts: OtherRoleActivityAlertFragment[]
    }

export const sortOldestFirst = <A extends { createdAt: string }>(
  alerts: readonly A[],
): A[] => [...alerts].sort((a, b) => compareAsc(a.createdAt, b.createdAt))

const otherRoleOf: Record<FccOperatorRoleType, FccOperatorRoleType> = {
  [FccOperatorRoleType.RiderSupport]:
    FccOperatorRoleType.RemoteAssistanceOperator,
  [FccOperatorRoleType.RemoteAssistanceOperator]:
    FccOperatorRoleType.RiderSupport,
}

export const getOtherRole = (
  role: FccOperatorRoleType | undefined,
): FccOperatorRoleType | undefined => (role ? otherRoleOf[role] : undefined)

export const getOtherRoleActivity = (
  vehicle: Partial<OtherRoleActivityVehicleFragment>,
  role: FccOperatorRoleType | undefined,
): OtherRoleActivity => {
  const otherRole = getOtherRole(role)
  if (!otherRole) {
    return { state: 'idle' }
  }

  const activeAlerts = sortOldestFirst(
    (vehicle.fccCurrentAdAlerts?.alerts ?? []).filter(
      alert =>
        alert.status !== FccAdAlertStatus.Closed &&
        isAlertVisibleToRole(alert, otherRole),
    ),
  )

  const handlingOperator = getHandlingOperatorForRole(vehicle, otherRole)
  if (handlingOperator) {
    return {
      state: 'handling',
      operator: {
        backofficeUserId: handlingOperator.backofficeUserId,
        backofficeUser: handlingOperator.backofficeUser,
      },
      alerts: activeAlerts,
    }
  }

  if (activeAlerts.length > 0) {
    return { state: 'requested', alerts: activeAlerts }
  }

  return { state: 'idle' }
}
